import Vue from 'vue';
import { flattenSlides } from '../LessonPlanController/slideTreeFunctions';
import { RevisionDisplayMode } from 'plant-common/src/models/revisionDisplayMode';
import {
	addPathToItem,
	buildNavigationMap
} from '../LessonPlanController/navigationFunctions';
import { walkTreeData } from 'he-tree-vue';
import { SlidesTree } from '../../models/models';
import {
	NavigationErrorNotifications
} from './navigationErrorNotifications';

export function createNavigation({
	slides,
	mode,
	activityState,
	interactivePresentationLocked,
	shouldIncludeHiddenSlides,
	getSlideActivityState,
	navigateToRootSlideIfNeeded,
	notify
}) {
	return new Vue({
		data() {
			return {
				sourceSlides: slides,
				selectedSlide: null,
				slidesTree: null,
				refreshKey: 0
			};
		},
		computed: {
			mode() {
				return mode();
			},
			activityState() {
				return activityState();
			},
			canGoNext() {
				if (this.selectedSlideIndex + 1 < this.slidesVisible.length) {
					const slideState = this.slideNavigationMap.find(
						slide => slide.id === this.slidesVisible[this.selectedSlideIndex + 1].id
					);
					return slideState.available;
				}

				return false;
			},
			canGoPrev() {
				if (this.selectedSlideIndex - 1 > -1) {
					const slideState = this.slideNavigationMap.find(
						slide => slide.id === this.slidesVisible[this.selectedSlideIndex - 1].id
					);
					return slideState.available;
				}

				return false;
			},
			slideArray() {
				this.refreshKey;
				if (!this.slidesTree) return [];

				if (shouldIncludeHiddenSlides()) {
					return flattenSlides(this.slidesTree.slides);
				}

				return flattenSlides(this.slidesTree.slides).filter(slide => !slide.hasToHideSlide);
			},
			slidesVisible() {
				if (this.mode !== RevisionDisplayMode.edit) return this.$removeHiddenSections(this.slideArray);

				return this.slideArray;
			},
			selectedSlideIndex() {
				if (this.selectedSlide) {
					return this.slidesVisible.findIndex(s => s.id === this.selectedSlide.id);
				} else {
					return null;
				}
			},
			$isNotReviewOrEditMode() {
				return this.mode !== RevisionDisplayMode.review && this.mode !== RevisionDisplayMode.edit;
			},
			/**
			 * Get navigation availability map
			 * @returns {*[{id, completion, available}]} Navigation map
			 */
			slideNavigationMap() {
				return buildNavigationMap(
					this.slideArray,
					this.activityState,
					interactivePresentationLocked(),
					this.activityState && this.$isNotReviewOrEditMode
				);
			}
		},
		watch: {
			'selectedSlide.skip': {
				immediate: false,
				handler(newValue, oldValue) {
					const skipPropHasChanged = newValue !== oldValue && newValue !== undefined && oldValue !== undefined;

					if (skipPropHasChanged) {
						walkTreeData(this.slidesTree.slides, (node, index, parent) => {
							addPathToItem(node, parent);
						});
					}
				}
			}
		},
		methods: {
			initSlidesTree() {
				this.slidesTree = new SlidesTree({
					slides: this.sourceSlides || []
				});
				walkTreeData(this.slidesTree.slides, (node, index, parent) => {
					addPathToItem(node, parent);
				});
			},
			refreshSlideArray() {
				this.refreshKey++;
			},
			$removeHiddenSections(slideArray) {
				return slideArray.filter(slide => !slide.skip);
			},
			nextSlide() {
				if (this.canGoNext) {
					this.selectSlideByIndex(this.selectedSlideIndex + 1);
				}
			},
			prevSlide() {
				if (this.canGoPrev) {
					this.selectSlideByIndex(this.selectedSlideIndex - 1);
				}
			},
			selectSlideByIndex(newIndex) {
				if (newIndex < this.slidesVisible.length && newIndex > -1) {
					this.selectedSlide = this.slidesVisible[newIndex];
					navigateToRootSlideIfNeeded(this.selectedSlide);
				}
			},
			goToNextAvailableSlide() {
				const slideArrayIndex = this.slideArray.findIndex(s => s.id === this.selectedSlide.id);
				let nextAvailableSlide = undefined;
				for (let index = slideArrayIndex; index < this.slideArray.length; index++) {
					if (!this.slideArray[index].skip) {
						nextAvailableSlide = this.slideArray[index];
						break;
					}
				}
				this.selectedSlide = nextAvailableSlide
					? nextAvailableSlide
					: this.slidesVisible[this.slidesVisible.length - 1];
			},
			selectSlideById(id) {
				const nextSlideState = this.slideNavigationMap.find(slide => slide.id === id);
				if (!nextSlideState) {
					notify(NavigationErrorNotifications.slideDoesNotExists);

					return;
				}

				if (!nextSlideState.available) {
					notify(NavigationErrorNotifications.canNotNavigate);

					return;
				}

				this.selectedSlide = this.slideArray.find(s => s.id === id);
				// Navigation from Quiz: Objectives Level (check if skipped)
				if (this.selectedSlide.skip && this.mode !== RevisionDisplayMode.edit) {
					const slideActivityState = getSlideActivityState(this.selectedSlide);
					// if: completion reason is optional => force navigation to show modal (skip / review) // else: navigate to the following one
					if (slideActivityState.completionReason !== 'optional') {
						this.goToNextAvailableSlide();
					}
				}
				navigateToRootSlideIfNeeded(this.selectedSlide);
			}
		}
	});
}
